import { WarningCallout, WarningCalloutVariant } from '@hungryroot/pho';
import { InventoryStatus } from '@hungryroot/rest';
import { type UseAlgoliaInventoryInterface } from '../../UseAlgoliaInventoryInterface';

interface Props {
  testid?: string;
  item: Pick<UseAlgoliaInventoryInterface, 'index_ingredients'>;
  status: InventoryStatus;
  restrictions?: string[];
}

const tagsToText: { [key: string]: string } = {
  has_meat: 'meat',
  has_fish: 'fish',
  has_shellfish: 'shellfish',
  has_dairy: 'dairy',
  has_egg: 'eggs',
  has_byproduct: 'honey',
  has_gluten: 'gluten',
  has_soy: 'soy',
  has_nut: 'nuts',
  has_peanut: 'peanuts',
};

function inventoryStatusValues(
  item: Props['item'],
  status:
    | InventoryStatus.Never
    | InventoryStatus.OutOfStock
    | InventoryStatus.Restrictions,
  restrictions?: Props['restrictions']
) {
  if (status === InventoryStatus.Never) {
    if (
      item.index_ingredients !== undefined &&
      item.index_ingredients.length > 0
    ) {
      return {
        text: `Contains items you set to 'never'`,
        variant: WarningCalloutVariant.Never,
      };
    }
    return { text: `You set to 'never'`, variant: WarningCalloutVariant.Never };
  }

  if (status === InventoryStatus.Restrictions && restrictions) {
    const mappedRestrictions = restrictions
      .filter((r) => tagsToText[r] !== undefined)
      .map((r) => tagsToText[r]);
    const overflow =
      mappedRestrictions.length - 2 > 0 ? mappedRestrictions.length - 2 : 0;
    const text = `Contains ${mappedRestrictions.slice(0, 2).join(', ')}${
      overflow > 0 ? ` + ${overflow} more` : ''
    }`;

    return { text: text, variant: WarningCalloutVariant.Restrictions };
  }

  return { text: `Out of stock`, variant: WarningCalloutVariant.OutOfStock };
}

export function SearchInventoryStatus(props: Props) {
  const { testid, item, status, restrictions } = props;

  if (status === InventoryStatus.Ideal) {
    return null;
  }

  const { text, variant } = inventoryStatusValues(item, status, restrictions);

  return (
    <WarningCallout
      testid={`${testid}-inventory-status`}
      text={text}
      status={status}
      variant={variant}
    />
  );
}
